import { HStack, PageContainer, Text, VStack } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { CrtImportHistoryButtons } from '@/components/CrtImportHistoryButtons'
import { CrtImportPeriodNotFound } from '@/components/CrtImportPeriodNotFound'
import { DetailsHeader } from '@/components/DetailsHeader'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import { Spinner } from '@/components/Spinner'
import {
  salesDealActualByPeriodQueryOptions,
  salesDealActualsDetailsSearchSchema,
  useSalesDealActualById,
  useUpdateSalesDealActuals,
} from '@/hooks/queries/crt/useSalesDealActuals'
import { useLastUpdatedSalesDealActualsLog } from '@/hooks/queries/useAuditTrails'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { useTablePagination } from '@/hooks/useTablePagination'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { crtPeriodFromParams } from '@/lib/utils/crtPeriodParams'
import { queryClient } from '@/main'
import { parseDateOnly } from '@/utils/DateOnlyHelpers'
import { UpdateSalesDealActuals } from '@repo/backend/crt'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SalesDealActualsDetailsDataTable from './-components/SalesDealActualsDetails/SalesDealActualsDetailsDataTable'
import { SalesDealActualsExportMenu } from './-components/SalesDealActualsExportMenu'

export const Route = createFileRoute(
  '/_auth/_modules/crt/customer-investments/data-sources/sales-deal-actuals/$year/$month',
)({
  component: SalesDealActualDetailsPeriod,
  validateSearch: (search) => salesDealActualsDetailsSearchSchema.parse(search),
  loader: async ({ params }) => {
    // The URL names the import's period — its month of billing — not a CRT period.
    const period = crtPeriodFromParams(params)
    const salesDealActual = period
      ? await queryClient.fetchQuery(salesDealActualByPeriodQueryOptions(period))
      : null

    return {
      crumb: salesDealActual?.name ?? `${params.year}/${params.month}`,
      period,
      salesDealActualId: salesDealActual ? String(salesDealActual.id) : null,
    }
  },
})

function SalesDealActualDetailsPeriod() {
  const { formatMonthYear } = useLocalizedDate()
  const { year, month } = Route.useParams()
  const { period, salesDealActualId } = Route.useLoaderData()

  if (!salesDealActualId) {
    return (
      <CrtImportPeriodNotFound
        period={period ? formatMonthYear(parseDateOnly(period)) : `${year}/${month}`}
        otherPeriodsTo="/crt/customer-investments/data-sources/sales-deal-actuals"
      />
    )
  }

  return <SalesDealActualDetails key={salesDealActualId} salesDealActualId={salesDealActualId} />
}

function SalesDealActualDetails({ salesDealActualId }: { salesDealActualId: string }) {
  const { t } = useTranslation(['crt', 'common'])
  const { formatMonthYear } = useLocalizedDate()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const { data, isPending, isError } = useSalesDealActualById(salesDealActualId, {
    ...search,
    page: search.page ?? 1,
    pageSize,
  })
  const { data: lastUpdatedSalesDealActualsTrail } =
    useLastUpdatedSalesDealActualsLog(salesDealActualId)

  const [formState, setFormState] = useState({
    name: '',
    description: '',
  })

  useEffect(() => {
    if (data) {
      setFormState({
        name: data.name,
        description: data.description ?? '',
      })
    }
  }, [data])

  const getUnsavedChanges = (): UpdateSalesDealActuals | null => {
    if (!data) return null

    const changes: UpdateSalesDealActuals = {}
    let hasChanges = false

    if (formState.name !== data.name) {
      changes.name = formState.name
      hasChanges = true
    }

    if (formState.description !== (data.description ?? '')) {
      changes.description = formState.description
      hasChanges = true
    }

    return hasChanges ? changes : null
  }

  const hasUnsavedChanges = getUnsavedChanges() !== null

  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedSalesDealActualsTrail)

  const { mutate: updateSalesDealActuals, isPending: isUpdatePending } = useUpdateSalesDealActuals({
    salesDealActualId: Number(salesDealActualId),
  })

  const handleUpdateName = (name: string) => {
    setFormState((prev) => ({ ...prev, name }))
  }

  const handleUpdateDescription = (description: string) => {
    setFormState((prev) => ({ ...prev, description }))
  }

  const handleSave = () => {
    const unsavedChanges = getUnsavedChanges()
    if (!unsavedChanges) return

    updateSalesDealActuals(unsavedChanges)
  }

  if (isPending) {
    return (
      <PageContainer className="flex items-center justify-center">
        <Spinner size="lg" />
      </PageContainer>
    )
  }

  if (isError || !data) {
    return (
      <PageContainer maxWidth="wide">
        <Text>{t('errors.salesDealActuals.fetchFailed')}</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="pb-28" maxWidth="wide">
      <Block shouldBlock={hasUnsavedChanges} />

      <HStack gap="sm" className="justify-between">
        <DetailsHeader
          name={formState.name}
          description={formState.description}
          onUpdateName={handleUpdateName}
          onUpdateDescription={handleUpdateDescription}
          permission="crt_sales_deal_actuals_edit"
        />

        <VStack gap="md" className="items-end">
          <Text className="text-muted-foreground text-sm">
            {formatMonthYear(parseDateOnly(data.period))}
          </Text>
          <HStack gap="sm">
            <SalesDealActualsExportMenu
              salesDealActualId={salesDealActualId}
              search={search}
              detailsRowCount={data.detailsRowCount}
              rawRowCount={data.detailsRawRowCount}
            />
            <CrtImportHistoryButtons
              otherPeriodsTo="/crt/customer-investments/data-sources/sales-deal-actuals"
              uploadType="import-sales-deal-actuals-details"
              uploadAttributes={{ 'sales-deal-actual-id': salesDealActualId }}
            />
          </HStack>
        </VStack>
      </HStack>

      <SalesDealActualsDetailsDataTable
        salesDealActualId={salesDealActualId}
        salesDealActual={data}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      <PermissionsGuard required={['crt_sales_deal_actuals_edit']}>
        <PendingChanges
          areChangesPending={hasUnsavedChanges}
          isSavePending={isUpdatePending}
          lastUpdatedLog={lastUpdatedLog}
          onSaveChanges={handleSave}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
